import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Typography,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useState } from 'react';

import './Dropdown.modules.scss';
import EditPrimaryIcon from '../../assets/Images/EditPrimaryIcon.svg';
import DeleteIcon from '../../assets/Images/deleteIconPurple.svg';
import ToolTipOnText from '../Tooltip/Tooltip';
import CloseIcon from '@mui/icons-material/Close';

interface IDropdownProps {
	name: any;
	itemList: any;
	label: any;
	valueSelector: string;
	selectedValue?: any;
	onChange?: any;
	ref?: any;
	labelName?: any;
	labelId?: any;
	disabled?: any;
	autoWidth?: any;
	variant?: any;
	id?: any;
	showIcons?: boolean;
	className?: string;
	onBlur?: any;
	onEditClick?: (item: any) => void;
	onDeleteClick?: (item: any) => void;
	inputShrink?: any;
	required?: any;
	placeholder?: string;
	defaultValue?: any;
	disableOption?: boolean;
	renderValue?: any;
	error?: any;
	errorMessage?: any;
	type?: any;
	outerClass?: any;
	paperClass?: string;
	removeArrow?: any;
	showTootip?: any;
	charActorLength?: any;
	showClearIcon?: boolean;
	hideLabel?: boolean;
}

const useStyles = makeStyles((theme: any) => ({
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	inputLabelRoot: {
		color:
			theme.palette.mode === 'dark'
				? 'rgba(255, 255, 255,0.3)'
				: 'rgba(0, 0, 0, 0.38)',
	},
}));

const Dropdown: React.FC<IDropdownProps> = (props) => {
	const theme: any = useTheme();
	const classes = useStyles();
	const [open, setOpen] = useState(false);

	const labelId = props.labelId || (props.label ? String(props.label).replace(/\s+/g, '-') : 'dropdown-label');

	const onChangeDropdown = (event: any) => {
		if (!props.onChange) return;
		props.onChange(event);
	};

	const is1920 = useMediaQuery('(max-width:1920px)');
	const is1366 = useMediaQuery('(max-width: 1366px)');
	const is1536 = useMediaQuery('(max-width:1536px)');
	const charActorLength = props.charActorLength ? props.charActorLength : 10;

	const renderTeamList = (itemList: any) =>
		itemList?.length > 0 &&
		itemList.map((item: any, index: any) => {
			const label = item[props.label] !== undefined ? item[props.label] : item;
			const shouldTruncate = (is1920 || is1366 || is1536) && props?.showTootip && typeof label === 'string' && label.length > charActorLength;

			return (
				<MenuItem
					key={label + index}
					className='dropdown__menu'
					value={
						item[props.valueSelector] !== undefined
							? item[props.valueSelector]
							: item
					}
					id={label}
					disabled={item.disableOption}
				>
					<ToolTipOnText
						title={shouldTruncate ? label : ''}
						placement='right'
					>
						<Typography
							variant='h6'
							component='div'
							className='dropdown__textExceed'
							sx={{
								flex: 1,
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							}}
						>
							{shouldTruncate ? `${label.slice(0, charActorLength)}...` : label}
						</Typography>
					</ToolTipOnText>

					{props.showIcons && (
						<div style={{ display: 'flex', marginLeft: '12px' }}>
							{props.onEditClick && (
								<IconButton
									onClick={(e) => {
										e.stopPropagation();
										setOpen(false);
										props.onEditClick?.(item);
									}}
								>
									<img
										src={EditPrimaryIcon}
										alt='edit'
										height={20}
										width={20}
									/>
								</IconButton>
							)}
							{props.onDeleteClick && (
								<IconButton
									onClick={(e) => {
										e.stopPropagation();
										setOpen(false);
										props.onDeleteClick?.(item);
									}}
								>
									<img src={DeleteIcon} alt='delete' height={20} width={20} />
								</IconButton>
							)}
						</div>
					)}
				</MenuItem>
			);
		});

	return (
		<div
			className={`${
				theme.palette.mode === 'dark' ? 'dropdownDark' : 'dropdown'
			} ${props.outerClass}`}
		>
			<FormControl
				variant='outlined'
				fullWidth
				error={props.error}
				size='small'
			>
				{!props.hideLabel && (
					<InputLabel
						classes={props.type ? { root: classes.inputLabelRoot } : {}}
						variant='outlined'
						id={props.labelId || 'demo-controlled-open-select-label'}
						required={props.required ?? false}
						sx={{ '& .MuiInputLabel-asterisk': { color: 'red' } }}
					shrink={
						props.selectedValue !== undefined && props.selectedValue !== '' && props.selectedValue !== null
							? true
							: props.inputShrink
					}
					>
						<>
							{props.labelName || ''}
						</>
					</InputLabel>
				)}

				<Select
					open={open}
					onOpen={() => setOpen(true)}
					onClose={() => setOpen(false)}
					label={!props.hideLabel ? props.labelName || '' : undefined}
					ref={props.ref}
					displayEmpty={!!props.hideLabel}
					className={props.className}
					IconComponent={
						!props.removeArrow ? KeyboardArrowDownIcon : undefined
					}
					labelId={props.labelId || 'demo-controlled-open-select-label'}
					fullWidth
					MenuProps={{
						classes: {
							paper: 'dropdown__menu__paper',
						},
					}}
					defaultValue={props.defaultValue}
					required={props.required ?? false}
					name={props.name ?? 'demo-simple-select'}
					id={props.id ?? 'demo-simple-select'}
					variant={props.variant ?? 'outlined'}
					value={props.selectedValue}
					onChange={onChangeDropdown}
					onBlur={props.onBlur ?? null}
					disabled={props.disabled ?? false}
					autoWidth={props.autoWidth ?? false}
					placeholder={props.placeholder}
					renderValue={
						props.showClearIcon
							? (selected: any) => {
									if (!selected) {
										return (
											<span style={{ opacity: 0.5 }}>
												{props.placeholder || 'Select'}
											</span>
										);
									}

									let content: any = selected;

									if (props.renderValue) {
										content = props.renderValue(selected);
									} else {
										const selectedItem = props.itemList?.find(
											(item: any) =>
												(item[props.valueSelector] !== undefined
													? item[props.valueSelector]
													: item) === selected
										);

										content = selectedItem
											? selectedItem[props.label] !== undefined
												? selectedItem[props.label]
												: selectedItem
											: selected;
									}

									if (!props.showClearIcon) return content;

									return (
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												width: '100%',
											}}
										>
											<div
												style={{
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}
											>
												{content}
											</div>

											{!props.disabled && (
												<IconButton
													size='small'
													onMouseDown={(e) => e.stopPropagation()}
													onClick={(e) => {
														e.stopPropagation();
														props.onChange?.({
															target: {
																name: props.name,
																value: '',
															},
														});
													}}
													style={{ marginLeft: 8, padding: 4 }}
												>
													<CloseIcon fontSize='small' />
												</IconButton>
											)}
										</div>
									);
							  }
							: props.renderValue
							? props.renderValue
							: (selected: any) => {
									const selectedItem = props.itemList?.find(
										(item: any) =>
											(item[props.valueSelector] !== undefined
												? item[props.valueSelector]
												: item) === selected
									);

									const displayText =
										selectedItem?.[props.label] || selectedItem || '';

									const showToolTip = typeof displayText === 'string' && displayText.length > 13;

									return (
										<>
											{showToolTip ? (
												<ToolTipOnText title={displayText} arrow>
													<Typography
														variant='body2'
														className='dropdown__textExceed'
														sx={{
															whiteSpace: 'nowrap',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
														}}
													>
														{displayText}
													</Typography>
												</ToolTipOnText>
											) : (
												displayText
											)}
										</>
									);
							  }
					}
				>
					{renderTeamList(props.itemList)}
				</Select>
			</FormControl>

			{props.error && (
				<FormHelperText
					style={{ marginLeft: '0.5rem', marginTop: '0rem', color: 'red' }}
				>
					{props.errorMessage || 'Required'}
				</FormHelperText>
			)}
		</div>
	);
};

export default Dropdown;
